import { ReactNode } from 'react';
import { Tooltip } from '@chakra-ui/react';
import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import StarIcon from '@mui/icons-material/Star';
import DatePeriod from '@/trio-ui/DatePeriod';
import Skill from '@/trio-ui/Skill';
import * as Styles from './ProfileInfoPreview.styles';
import Menu from '../Menu';
import TextEditor from '../TextEditor';

export type ProfileInfoPreviewProps = {
  title: string;
  subtitle?: string;
  link?: string;
  detail?: string;
  description?: string;
  skills?: string[];
  fromMonth?: string;
  fromYear?: string;
  toMonth?: string;
  toYear?: string;
  datePeriodLabel?: string;
  tooltipMessage?: string;
  tooltipIcon?: ReactNode;
  showToPeriod?: boolean;
  onEditInfo: () => void;
  onDelete: () => void;
};

export default function ProfileInfoPreview({
  title,
  link,
  subtitle,
  detail,
  description,
  skills,
  fromMonth,
  fromYear,
  toMonth,
  toYear,
  tooltipIcon = <StarIcon fontSize="small" />,
  tooltipMessage,
  datePeriodLabel,
  onEditInfo,
  onDelete,
}: ProfileInfoPreviewProps): JSX.Element {
  return (
    <Styles.Box>
      <Styles.HeaderRow>
        <Styles.Row>
          <Styles.Title>{title}</Styles.Title>

          {link && (
            <Styles.Link href={link} target="_blank" rel="noreferrer">
              <LinkIcon color="primary" fontSize="small" />
              <span>{link}</span>
            </Styles.Link>
          )}

          {subtitle && <Styles.Subtitle>{subtitle}</Styles.Subtitle>}

          {!!tooltipMessage && (
            <Styles.TooltipIcon>
              <Tooltip label={tooltipMessage} placement="right">
                {tooltipIcon}
              </Tooltip>
            </Styles.TooltipIcon>
          )}
        </Styles.Row>

        <Menu
          icon={MoreHorizIcon}
          menuId="profile-info-preview-menu"
          menuItems={[
            {
              label: 'Edit',
              menuItemIcon: CreateIcon,
              callback: onEditInfo,
            },
            {
              label: 'Delete',
              menuItemOptions: { warning: true },
              menuItemIcon: DeleteIcon,
              callback: (event) => {
                event.stopPropagation();
                onDelete();
              },
            },
          ]}
        />
      </Styles.HeaderRow>

      {!!detail && (
        <Styles.Row>
          <Styles.Text>{detail}</Styles.Text>
        </Styles.Row>
      )}

      {!!skills && (
        <Styles.Row>
          {skills.map((skill) => (
            <Skill key={skill} name={skill} />
          ))}
        </Styles.Row>
      )}
      {fromMonth && fromYear && (
        <Styles.Row>
          <DatePeriod
            from={{
              month: fromMonth,
              year: fromYear,
            }}
            to={{
              month: toMonth,
              year: toYear,
            }}
            actionLabel={datePeriodLabel}
          />
        </Styles.Row>
      )}

      {!!description && (
        <TextEditor hideButtons hideEditButton value={description} isEditing={false} />
      )}
    </Styles.Box>
  );
}
